<script setup lang="ts">
  import {
    OrganisationsService,
    UsersService,
    FormsService,
    CallsService,
    PhoneNumbersService,
    StripeInvoicesService,
    QuestionnairesService
  } from '@/types/generated'
  import { onMounted, ref, computed, watch } from 'vue'
  import {
    UsersIcon,
    ChevronRightIcon,
    DocumentIcon,
    PhoneIcon,
    QuestionMarkCircleIcon
  } from '@heroicons/vue/24/outline'
  import { ROUTES } from '@/router/routes'
  import { useUserStore } from '@/store/admin/user'

  const userStore = useUserStore()
  const user = computed(() => userStore.user)

  interface Item {
    title: string
    route: string | null
    count: number | null
    icon: any
    fetchCount: () => Promise<number>
    requiredPermission?: string
  }

  const items = ref<Item[]>([
    {
      title: 'Customers',
      route: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST,
      count: null,
      icon: UsersIcon,
      fetchCount: async () => {
        return (await OrganisationsService.organisationsList()).count
      }
    },
    {
      title: 'Invoices',
      route: ROUTES.ADMIN.PAYMENTS.INVOICES_LIST,
      count: null,
      icon: DocumentIcon,
      fetchCount: async () => {
        return (await StripeInvoicesService.stripeInvoicesList()).count
      },
      requiredPermission: 'is_payments_superuser'
    },
    {
      title: 'Users',
      route: ROUTES.ADMIN.USERS.USER_LIST,
      count: null,
      icon: UsersIcon,
      fetchCount: async () => {
        return (await UsersService.usersList()).count
      }
    },
    {
      title: 'Documents',
      route: ROUTES.ADMIN.DOCUMENTS.DOCUMENTS_LIST,
      count: null,
      icon: UsersIcon,
      fetchCount: async () => {
        return (await FormsService.formsList()).count
      }
    },
    {
      title: 'Calls',
      route: ROUTES.ADMIN.VOICE.CALL_LIST,
      count: null,
      icon: PhoneIcon,
      fetchCount: async () => {
        return (await CallsService.callsList()).count
      }
    },
    {
      title: 'Phone Numbers',
      route: ROUTES.ADMIN.VOICE.PHONE_NUMBER_LIST,
      count: null,
      icon: PhoneIcon,
      fetchCount: async () => {
        return (await PhoneNumbersService.phoneNumbersList()).count
      }
    },
    {
      title: 'Questionnaires',
      route: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_LIST,
      count: null,
      icon: QuestionMarkCircleIcon,
      fetchCount: async () => {
        return (await QuestionnairesService.questionnairesList()).count
      }
    }
  ])

  const availableItems = computed<Item[]>(() => {
    return items.value.filter((item) => {
      if (item.requiredPermission) {
        // @ts-ignore
        return user.value?.[item.requiredPermission]
      }
      return true
    })
  })

  watch(user, async (newUser) => {
    if (newUser) {
      for (const item of availableItems.value) {
        item.count = await item.fetchCount()
      }
    }
  })
</script>

<template>
  <div v-if="user">
    <div class="grid grid-cols-2 gap-2 p-5 md:grid-cols-4 md:gap-4">
      <div
        v-for="item in availableItems"
        :key="item.title"
        class="group rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5
          hover:shadow-lg focus:-translate-y-0.5 focus:shadow-lg focus:outline-none"
      >
        <div class="flex gap-x-3">
          <div class="grow">
            <h2 class="text-xs text-gray-600">
              {{ item.title }}
            </h2>
            <p class="text-xl font-semibold text-gray-800">
              {{ item.count }}
            </p>
          </div>
          <component
            :is="item.icon"
            class="size-6 flex-shrink-0 text-gray-500"
          />
        </div>
        <router-link
          v-if="item.route"
          class="mt-3 inline-flex items-center gap-x-1 text-sm font-medium text-purple-600
            group-hover:text-purple-500 group-focus:text-purple-500"
          :to="item.route"
        >
          Show {{ item.title.toLowerCase() }}
          <ChevronRightIcon class="size-3 flex-shrink-0" />
        </router-link>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
